// Thời gian giữ chỗ draft đặt phòng /sale — file THUẦN (không firebase-admin) để client dùng chung với server.
import type { Booking } from '@/types';

/** Mặc định 30': giữ lâu quá khách để trôi/nghĩ lại (góp ý TA Tuấn Anh 02/10/2026); tenant đổi ở Cài đặt → Đặt phòng online (5..1440). */
export const DEFAULT_HOLD_MINUTES = 30;

/** "30 phút" / "2 giờ" / "1 giờ 30 phút" / "24 giờ" — mọi chỗ hiện thời gian giữ chỗ (UI khách + Telegram). */
export function fmtHold(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) return `${minutes / 60} giờ`;
  if (minutes > 60) return `${Math.floor(minutes / 60)} giờ ${minutes % 60} phút`;
  return `${minutes} phút`;
}

// ── "Booking này có đang chiếm phòng không?" ────────────────────────────────
// Dùng chung server (/api/sale/search, /api/sale/timeline, lib/sale-booking.ts) VÀ client
// (app/(app)/timeline/page.tsx) — trước 2026-10-03 trang Timeline nội bộ KHÔNG dùng hàm này, tự so
// `checkIn/checkOut` trần nên draft khách tự đặt trên /sale quá hạn giữ chỗ (`holdMinutes`, mặc định
// 30') KHÔNG BAO GIỜ tự nhả phòng trên Timeline dù `/sale` đã coi là trống từ lâu — NV nhìn hàng "Trống"
// hoặc "Kiểm tra phòng" thấy 0 phòng trống dù thực ra có phòng đã bỏ cọc/bỏ đi giữa chừng từ hôm trước.
const NON_BLOCKING = new Set(['cancelled', 'no_show', 'deleted']);
const LEGACY_HOLD_MS = 24 * 3_600_000; // draft tạo TRƯỚC khi có `saleHoldUntil` — giữ 24h như cũ

export type AvailabilityBlocker = Pick<Booking, 'status'> & { createdVia?: string; createdAt?: unknown; saleHoldUntil?: unknown };

function createdMs(b: AvailabilityBlocker): number {
  return (b.createdAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0;
}

/** Booking này có đang chiếm phòng không? Draft do khách tự đặt trên /sale quá thời gian giữ chỗ chưa
 *  thanh toán = hết hạn giữ chỗ, KHÔNG còn chặn khách khác (không thì 1 người bấm tới màn QR rồi bỏ đi
 *  sẽ khoá phòng mãi). NV vẫn thấy draft ở /bookings + thanh booking trên Timeline (viền gạch + 📝). */
export function blocksAvailability(b: AvailabilityBlocker, nowMs = Date.now()): boolean {
  if (NON_BLOCKING.has(b.status)) return false;
  if (b.status === 'draft' && b.createdVia === 'sale_web') {
    // `saleHoldUntil` ghi lúc tạo (theo cấu hình tenant tại thời điểm đó); draft cũ chưa có field → createdAt + 24h
    const until = (b.saleHoldUntil as { toMillis?: () => number } | undefined)?.toMillis?.();
    if (until != null) { if (nowMs > until) return false; }
    else { const c = createdMs(b); if (c > 0 && nowMs - c > LEGACY_HOLD_MS) return false; }
  }
  return true;
}

/** Đặt phòng online có bật cho toà này không: ghi đè theo toà (`buildingEnabled[code]`) đè lên công tắc chung (`enabled`). File thuần — dùng cả client lẫn server. */
export function saleBookingEnabledFor(info: { enabled: boolean; buildingEnabled?: Record<string, boolean> }, buildingCode?: string): boolean {
  const o = buildingCode ? info.buildingEnabled?.[buildingCode] : undefined;
  return o ?? info.enabled;
}
